import { useMemo, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router';

import { useRetryProcessor } from '@/api/retry-processor/hooks';
import { PageContainer } from '@/components/layout/page-container';
import { PageHeader } from '@/components/layout/page-header';
import { Skeleton } from '@/components/ui/skeleton';
import { AttemptsTable } from './_components/attempts-table';
import {
	StatusFilterChips,
	statusesForCategory,
} from './_components/status-filter-chips';

const RETRY_DESCRIPTION =
	'Delivery retries in flight, recovered, and exhausted.';

// Retry-processor detail screen (task [3.5], ws-ir82u). Mirrors the overview
// dashboard's mock/real duality with ZERO page-level branching — the hook owns
// that seam. Colored stat tiles + a client-side searchable attempts table.
export default function RetryProcessorPage() {
	const navigate = useNavigate();
	const { data, isLoading, isError, error, refetch } = useRetryProcessor();
	const [query, setQuery] = useState('');
	const [activeCategory, setActiveCategory] = useState<string | null>(null);

	// Search-narrowed set feeds ONLY the chip badge counts — the table applies
	// its own search + status filter from the raw attempts prop, so the two
	// paths never double-filter or drift.
	const searchFiltered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!data || !q) return data?.attempts ?? [];
		return data.attempts.filter((a) =>
			[a.recipient, a.provider, a.status, a.errorReason].some((field) =>
				field.toLowerCase().includes(q),
			),
		);
	}, [data, query]);

	if (isLoading) {
		return (
			<PageContainer>
				<PageHeader title="Retry Processor" description={RETRY_DESCRIPTION} />
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{Array.from({ length: 4 }).map((_, i) => (
						<Skeleton key={i} className="h-28 rounded-md" />
					))}
				</div>
				<Skeleton className="h-96 rounded-lg" />
			</PageContainer>
		);
	}

	if (isError || !data) {
		return (
			<PageContainer>
				<PageHeader title="Retry Processor" description={RETRY_DESCRIPTION} />
				<div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-card py-16 text-center">
					<AlertCircle className="size-10 text-danger" aria-hidden="true" />
					<p className="text-sm text-muted-foreground">
						{error instanceof Error
							? error.message
							: 'Failed to load retry-processor data.'}
					</p>
					<button
						type="button"
						onClick={() => void refetch()}
						className="rounded-none border border-border-strong px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
					>
						Try again
					</button>
				</div>
			</PageContainer>
		);
	}

	return (
		<PageContainer>
			<PageHeader title="Retry Processor" description={RETRY_DESCRIPTION} />
			<StatusFilterChips
				attempts={searchFiltered}
				active={activeCategory}
				onSelect={setActiveCategory}
			/>
			<AttemptsTable
				attempts={data.attempts}
				query={query}
				onQueryChange={setQuery}
				statusFilter={statusesForCategory(activeCategory)}
				onRowClick={(attempt) =>
					void navigate(`/retry-processor/${attempt.id}`, {
						viewTransition: true,
						state: { attempt },
					})
				}
			/>
		</PageContainer>
	);
}
